import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useCart } from "@/contexts/CartContext";
import { useAuth } from "@/contexts/AuthContext";
import { resolveImage, formatRM } from "@/lib/api";
import { Trash2, Minus, Plus, CalendarDays, Tag } from "lucide-react";

export default function Cart() {
  const { t, i18n } = useTranslation();
  const zh = !i18n.language?.startsWith("en");
  const { items, updateQuantity, removeItem, total, count, productSubtotal, eventsSubtotal } = useCart();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const goCheckout = () => {
    if (authLoading) return;
    if (!user) {
      navigate("/login?next=/checkout");
    } else {
      navigate("/checkout");
    }
  };

  const keyOf = (it) => (it.type === "event" ? `event:${it.event_id}` : `product:${it.product_id}`);

  return (
    <div className="mx-auto max-w-5xl px-6 lg:px-10 py-16" data-testid="cart-page">
      <h1 className="font-serif-cn text-4xl text-[#003645] font-semibold tracking-tight">
        {t("cart.title")}
      </h1>

      {items.length === 0 ? (
        <div className="mt-10 rounded-lg border border-[#e5ebeb] bg-white p-16 text-center">
          <div className="font-serif-cn text-2xl text-[#003645]">{t("cart.empty")}</div>
          <p className="mt-3 text-[#4a6e77]">{t("cart.emptyDesc")}</p>
          <Link
            to="/products"
            className="mt-8 inline-block px-6 py-3 rounded-full bg-[#005870] text-white font-medium hover:bg-[#003645] transition-colors duration-200"
            data-testid="empty-cart-continue"
          >
            {t("cart.continueShopping")}
          </Link>
        </div>
      ) : (
        <div className="mt-10 grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2 space-y-4" data-testid="cart-items">
            {items.map((it) => {
              const name = zh ? it.name_zh : it.name_en;
              const isEvent = it.type === "event";
              const k = keyOf(it);
              return (
                <div
                  key={k}
                  className="flex gap-4 rounded-lg border border-[#e5ebeb] bg-white p-4"
                  data-testid={`cart-item-${isEvent ? "event-" : ""}${isEvent ? it.event_id : it.product_id}`}
                >
                  <div className="h-24 w-24 rounded-md overflow-hidden bg-[#f5f0e8] shrink-0 flex items-center justify-center">
                    {it.image ? (
                      <img
                        src={resolveImage(it.image)}
                        alt={name}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <CalendarDays size={24} className="text-[#4a6e77]" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#00868e] font-semibold">
                      {isEvent ? <CalendarDays size={11} /> : <Tag size={11} />}
                      {isEvent ? t("cartTypes.event") : t("cartTypes.product")}
                    </div>
                    <div className="font-serif-cn text-[#003645] font-medium truncate">
                      {name}
                    </div>
                    {isEvent && it.starts_at && (
                      <div className="text-xs text-[#4a6e77] mt-0.5">
                        {new Date(it.starts_at).toLocaleString()}
                      </div>
                    )}
                    <div className="text-sm text-[#4a6e77] mt-1">
                      {formatRM(it.unit_price)}
                    </div>
                    <div className="mt-3 flex items-center gap-3">
                      <div className="inline-flex items-center border border-[#e5ebeb] rounded-full">
                        <button
                          onClick={() => updateQuantity(k, Math.max(1, it.quantity - 1))}
                          className="h-8 w-8 flex items-center justify-center text-[#4a6e77] hover:text-[#005870]"
                        >
                          <Minus size={12} />
                        </button>
                        <span className="w-8 text-center text-sm font-medium">{it.quantity}</span>
                        <button
                          onClick={() => updateQuantity(k, it.quantity + 1)}
                          className="h-8 w-8 flex items-center justify-center text-[#4a6e77] hover:text-[#005870]"
                        >
                          <Plus size={12} />
                        </button>
                      </div>
                      <button
                        onClick={() => removeItem(k)}
                        className="text-[#4a6e77] hover:text-red-600 transition-colors duration-200"
                        aria-label={t("cart.remove")}
                        data-testid={`remove-${isEvent ? "event-" : ""}${isEvent ? it.event_id : it.product_id}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                  <div className="text-right font-semibold text-[#005870] shrink-0">
                    {formatRM(it.unit_price * it.quantity)}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="lg:col-span-1">
            <div className="rounded-lg border border-[#e5ebeb] bg-white p-6 sticky top-24" data-testid="cart-summary">
              <div className="text-xs uppercase tracking-[0.3em] text-[#00868e] font-semibold">
                summary
              </div>
              <div className="mt-4 flex justify-between text-sm text-[#4a6e77]">
                <span>{t("cart.itemsCount", { n: count })}</span>
              </div>
              {productSubtotal > 0 && eventsSubtotal > 0 && (
                <div className="mt-3 space-y-1 text-xs text-[#4a6e77]">
                  <div className="flex justify-between"><span>{t("cartTypes.product")}</span><span>{formatRM(productSubtotal)}</span></div>
                  <div className="flex justify-between"><span>{t("cartTypes.event")}</span><span>{formatRM(eventsSubtotal)}</span></div>
                </div>
              )}
              <div className="mt-2 flex justify-between text-lg font-semibold text-[#003645]">
                <span>{t("common.total")}</span>
                <span data-testid="cart-total">{formatRM(total)}</span>
              </div>
              <button
                onClick={goCheckout}
                disabled={authLoading}
                className="mt-6 w-full px-6 py-3 rounded-full bg-[#005870] text-white font-medium hover:bg-[#003645] transition-colors duration-200 disabled:opacity-60"
                data-testid="checkout-btn"
              >
                {t("cart.checkout")}
              </button>
              <Link
                to="/products"
                className="mt-3 block text-center text-sm text-[#4a6e77] hover:text-[#005870] transition-colors duration-200"
              >
                {t("cart.continueShopping")}
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
